// Service to fetch profile data from various APIs
import { apiEndpoints } from '../data/resumeData';

export class ProfileService {
  // Get Hugging Face headers with token if available
  static getHuggingFaceHeaders() {
    const token = process.env.REACT_APP_HUGGINGFACE_TOKEN;
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  }

  // Fetch GitHub profile data
  static async getGitHubProfile() {
    try {
      const response = await fetch(apiEndpoints.github);
      if (!response.ok) throw new Error('Failed to fetch GitHub profile');
      
      const data = await response.json();
      return {
        username: data.login,
        name: data.name,
        bio: data.bio,
        publicRepos: data.public_repos,
        followers: data.followers,
        following: data.following,
        location: data.location,
        company: data.company,
        blog: data.blog,
        avatarUrl: data.avatar_url,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error fetching GitHub profile:', error);
      return null;
    }
  }

  // Fetch GitHub repositories
  static async getGitHubRepos() {
    try {
      const response = await fetch(apiEndpoints.githubRepos);
      if (!response.ok) throw new Error('Failed to fetch GitHub repos');
      
      const repos = await response.json();
      return repos.slice(0, 10).map(repo => ({
        name: repo.name,
        description: repo.description,
        language: repo.language,
        stars: repo.stargazers_count,
        forks: repo.forks_count,
        url: repo.html_url,
        updatedAt: repo.updated_at,
        isFork: repo.fork
      }));
    } catch (error) {
      console.error('Error fetching GitHub repos:', error);
      return [];
    }
  }

  // Fetch Hugging Face profile data
  static async getHuggingFaceProfile() {
    try {
      const headers = this.getHuggingFaceHeaders();
      
      // Try multiple Hugging Face API endpoints
      const endpoints = [
        'https://huggingface.co/api/users/Dhrumit1314',
        'https://huggingface.co/api/users/dhrumit1314', // Try lowercase
        'https://huggingface.co/api/users/Dhrumit1314/profile' // Try profile endpoint
      ];

      let response = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          response = await fetch(endpoint, { 
            headers,
            method: 'GET'
          });
          
          if (response.ok) {
            break; // Success, exit the loop
          } else if (response.status === 404) {
            console.warn(`User not found at ${endpoint}`);
            continue; // Try next endpoint
          }
        } catch (error) {
          lastError = error;
          console.warn(`Failed to fetch from ${endpoint}:`, error.message);
          continue; // Try next endpoint
        }
      }

      // If all endpoints failed, return null instead of throwing
      if (!response || !response.ok) {
        console.warn('All Hugging Face endpoints failed, returning null');
        return null;
      }
      
      const data = await response.json();
      
      // Handle different response formats from Hugging Face API
      return {
        username: data.name || data.username || 'Dhrumit1314',
        displayName: data.displayName || data.name || 'Dhrumit1314',
        bio: data.bio || data.description || 'No bio available',
        followers: data.followers || 0,
        following: data.following || 0,
        avatarUrl: data.avatarUrl || data.avatar_url || null,
        joinedAt: data.dateJoined || data.created_at || null,
        // Additional fields that might be available
        location: data.location || null,
        website: data.website || null,
        organization: data.organization || null,
        // Enhanced fields with token access
        email: data.email || null,
        fullname: data.fullname || null,
        canPay: data.canPay || false,
        pro: data.pro || false
      };
    } catch (error) {
      console.error('Error fetching Hugging Face profile:', error);
      return null; // Return null instead of throwing
    }
  }

  // Fetch Hugging Face models (if available)
  static async getHuggingFaceModels() {
    try {
      const headers = this.getHuggingFaceHeaders();
      const modelsUrl = 'https://huggingface.co/api/models?author=Dhrumit1314';
      const response = await fetch(modelsUrl, { headers });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('No models found for user Dhrumit1314');
          return [];
        }
        throw new Error('Failed to fetch Hugging Face models');
      }
      
      const models = await response.json();
      return models.slice(0, 10).map(model => ({
        name: model.modelId,
        description: model.description || 'No description',
        downloads: model.downloads || 0,
        likes: model.likes || 0,
        tags: model.tags || [],
        lastModified: model.lastModified,
        // Enhanced fields with token access
        pipeline_tag: model.pipeline_tag || null,
        library_name: model.library_name || null,
        private: model.private || false,
        gated: model.gated || false
      }));
    } catch (error) {
      console.error('Error fetching Hugging Face models:', error);
      return [];
    }
  }

  // Fetch Hugging Face datasets (if available)
  static async getHuggingFaceDatasets() {
    try {
      const headers = this.getHuggingFaceHeaders();
      const datasetsUrl = 'https://huggingface.co/api/datasets?author=Dhrumit1314';
      const response = await fetch(datasetsUrl, { headers });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('No datasets found for user Dhrumit1314');
          return [];
        }
        throw new Error('Failed to fetch Hugging Face datasets');
      }
      
      const datasets = await response.json();
      return datasets.slice(0, 5).map(dataset => ({
        name: dataset.id,
        description: dataset.description || 'No description',
        downloads: dataset.downloads || 0,
        likes: dataset.likes || 0,
        tags: dataset.tags || [],
        lastModified: dataset.lastModified,
        private: dataset.private || false,
        gated: dataset.gated || false
      }));
    } catch (error) {
      console.error('Error fetching Hugging Face datasets:', error);
      return [];
    }
  }

  // Fetch Hugging Face spaces (if available)
  static async getHuggingFaceSpaces() {
    try {
      const headers = this.getHuggingFaceHeaders();
      const spacesUrl = 'https://huggingface.co/api/spaces?author=Dhrumit1314';
      const response = await fetch(spacesUrl, { headers });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn('No spaces found for user Dhrumit1314');
          return [];
        }
        throw new Error('Failed to fetch Hugging Face spaces');
      }
      
      const spaces = await response.json();
      return spaces.slice(0, 5).map(space => ({
        name: space.id,
        title: space.title || space.id,
        description: space.description || 'No description',
        downloads: space.downloads || 0,
        likes: space.likes || 0,
        tags: space.tags || [],
        lastModified: space.lastModified,
        private: space.private || false,
        gated: space.gated || false,
        sdk: space.sdk || null
      }));
    } catch (error) {
      console.error('Error fetching Hugging Face spaces:', error);
      return [];
    }
  }

  // Fetch all profile data
  static async getAllProfileData() {
    try {
      const [githubProfile, githubRepos, huggingfaceProfile, huggingfaceModels, huggingfaceDatasets, huggingfaceSpaces] = await Promise.allSettled([
        this.getGitHubProfile(),
        this.getGitHubRepos(),
        this.getHuggingFaceProfile(),
        this.getHuggingFaceModels(),
        this.getHuggingFaceDatasets(),
        this.getHuggingFaceSpaces()
      ]);

      return {
        github: githubProfile.status === 'fulfilled' ? githubProfile.value : null,
        githubRepos: githubRepos.status === 'fulfilled' ? githubRepos.value : [],
        huggingface: huggingfaceProfile.status === 'fulfilled' ? huggingfaceProfile.value : null,
        huggingfaceModels: huggingfaceModels.status === 'fulfilled' ? huggingfaceModels.value : [],
        huggingfaceDatasets: huggingfaceDatasets.status === 'fulfilled' ? huggingfaceDatasets.value : [],
        huggingfaceSpaces: huggingfaceSpaces.status === 'fulfilled' ? huggingfaceSpaces.value : []
      };
    } catch (error) {
      console.error('Error fetching all profile data:', error);
      return {
        github: null,
        githubRepos: [],
        huggingface: null,
        huggingfaceModels: [],
        huggingfaceDatasets: [],
        huggingfaceSpaces: []
      };
    }
  }

  // Format profile data for AI context
  static formatProfileDataForAI(profileData) {
    let formattedData = '';

    if (profileData.github) {
      formattedData += `
### 🐙 GitHub Profile Information
- **Username:** ${profileData.github.username}
- **Name:** ${profileData.github.name || 'Not specified'}
- **Bio:** ${profileData.github.bio || 'No bio available'}
- **Public Repositories:** ${profileData.github.publicRepos}
- **Followers:** ${profileData.github.followers}
- **Following:** ${profileData.github.following}
- **Location:** ${profileData.github.location || 'Not specified'}
- **Company:** ${profileData.github.company || 'Not specified'}
- **Member since:** ${new Date(profileData.github.createdAt).toLocaleDateString()}
`;
    }

    if (profileData.githubRepos && profileData.githubRepos.length > 0) {
      formattedData += `
### 🚀 Top GitHub Repositories
${profileData.githubRepos.slice(0, 5).map(repo => 
  `• **${repo.name}** - *${repo.description || 'No description'}* - \`${repo.language || 'Not specified'}\` - ⭐ **${repo.stars}** 🍴 **${repo.forks}**`
).join('\n')}
`;
    }

    if (profileData.huggingface) {
      formattedData += `
### 🤗 Hugging Face Profile Information
- **Username:** ${profileData.huggingface.username}
- **Display Name:** ${profileData.huggingface.displayName || 'Not specified'}
- **Bio:** ${profileData.huggingface.bio || 'No bio available'}
- **Followers:** ${profileData.huggingface.followers}
- **Following:** ${profileData.huggingface.following}
- **Member since:** ${profileData.huggingface.joinedAt ? new Date(profileData.huggingface.joinedAt).toLocaleDateString() : 'Not specified'}
- **Pro User:** ${profileData.huggingface.pro ? 'Yes' : 'No'}
`;
    }

    if (profileData.huggingfaceModels && profileData.huggingfaceModels.length > 0) {
      formattedData += `
### 🤖 Hugging Face Models
${profileData.huggingfaceModels.map(model => 
  `• **${model.name}** - *${model.description}* - 📥 **${model.downloads}** ❤️ **${model.likes}** ${model.private ? '(Private)' : ''}`
).join('\n')}
`;
    }

    if (profileData.huggingfaceDatasets && profileData.huggingfaceDatasets.length > 0) {
      formattedData += `
### 📊 Hugging Face Datasets
${profileData.huggingfaceDatasets.map(dataset => 
  `• **${dataset.name}** - *${dataset.description}* - 📥 **${dataset.downloads}** ❤️ **${dataset.likes}**`
).join('\n')}
`;
    }

    if (profileData.huggingfaceSpaces && profileData.huggingfaceSpaces.length > 0) {
      formattedData += `
### 🚀 Hugging Face Spaces
${profileData.huggingfaceSpaces.map(space => 
  `• **${space.name}** - *${space.description}* - 📥 **${space.downloads}** ❤️ **${space.likes}** (\`${space.sdk || 'Unknown SDK'}\`)`
).join('\n')}
`;
    }

    return formattedData;
  }
}